// dependencies
import { ApolloClient } from "@apollo/client";
import { ColumnState } from "ag-grid-community";
import Fuse, { FuseOptions } from "fuse.js";

import { IFeatureFlag } from "@performyard/models";

import { DataSet } from "../../../../../components/ColumnPicker";
import { ModalInfo } from "../../../components/ReportInfoModal";

// Schemas
import { EmployeeField } from "../../../schemas/employee";
import {
	ChartOptions,
	FieldFilter,
	FilterOptions,
	GridColumn,
	GridSort,
	IAvailableSummarizeField,
	IColumnMetadata,
	IReportPaginationParams,
	ISummaryColumnMetadata,
	Report,
	SelectedFilter,
	SummaryFunction,
} from "../../../schemas/report";

import { IQuestion, ISurveyQuestion } from "../Components/QuestionGroupingModal/schema";
import { ChartType } from "./enums";

type Modal = "QUESTION_GROUP" | "SAVE_AS" | "EDIT" | null;
export enum UIState {
	INITIALIZING = "INITIALIZING",
	FETCHING = "FETCHING",
	READY = "READY",
	ERROR = "ERROR",
}
export type QuestionGroupingModalState = "NEW" | "EDIT";

export enum UserRole {
	admin = "admin",
	manager = "manager",
}
export interface CurrentUserContext {
	userId: string;
	directReports: string[] | [];
	userRole: UserRole;
}

export interface ReportBaseStore {
	init: (reportID: string) => Promise<void>;

	// some ui states
	uiState: UIState;
	setUIState: (state: UIState) => void;

	// GraphQL client where queries are handled. We need a client in order
	// to fetch data from the server.
	client: ApolloClient<any> | null;
	setClient: (client: any) => void;

	// The report ID is fetched from the URL and should be set immediately
	reportID: string | null;
	setReportID: (reportID: string) => void;

	getInitialColumns: () => string[];

	// The report is fetched from the server its data is stored in this state
	report: Report | null;
	fetchReport: () => Promise<void>;
	fetchReportCSV: () => Promise<void>;
	resetCSVJobID: () => void;
	csvJobID: string | null;
	setReportFields: (updates: { field: string; value: any }[]) => void;

	// Save and save as functions for the report
	saveReport: () => Promise<void>;
	saveAsReport: (modalInfo: ModalInfo) => Promise<void>;

	// keep track of any changes so we can inform the user if they are
	// exiting without saving
	hasChanges: boolean;
	getHasChanges: () => boolean;
	setHasChanges: (hasChanges: boolean) => void;

	// grid data
	gridData: any[];

	// feature flags
	companyFeatureFlags: IFeatureFlag["_id"][];

	// user permissions need to be accessed in multiple places
	userPermissions: string[];
	setUserPermissions: (userPermissions: string[]) => void;

	// sharedWith
	sharedWith: string[];
	setSharedWith: (sharedWith: string[]) => void;

	error?: Error;

	// Count
	gridDataCount: number;
	setGridDataCount: (count: number) => void;

	// sort
	sorts: GridSort[];
	_formatGridSorts: (sorts) => GridSort[];
	setSorts: (sorts: { colId: string; direction: "asc" | "desc" }[]) => void;
	// getGridSorts: () => { colId: string; sort: string; sortIndex: number; }[];
	getGridSorts: () => ColumnState[];
	validateAndFilterSorts: (
		sorts: { colId: string; direction: "asc" | "desc" }[]
	) => { colId: string; direction: "asc" | "desc" }[];

	// filter data
	selectedQuickFilter: SelectedFilter[];
	setSelectedQuickFilter: (quickFilter: SelectedFilter[]) => void;
	setAdditionalFilters: (additionalFilters: FieldFilter[][]) => void;
	appliedFiltersCount: number;
	applySelectedQuickFilter: () => void;
	getAppliedFilters: () => FieldFilter[][];

	filterOptions: FilterOptions | null;
	setFilterOptions: (filterOptions: FilterOptions) => void;
	getFilterOptions: () => Promise<FilterOptions>;

	// These are the questions groups that are fetched from the server
	groupingSession:
		| {
				reviewQueryID: string | null;
				employeeQueryID: string | null;
		  }
		| any;
	groupedQuestions: any[];
	fetchGroupedQuestions: () => Promise<void>;
	ungroupGroupedQuestion: (groupID: string) => Promise<void>;

	// tracks the current active modal
	activeModal: Modal;
	setActiveModal: (questions: Modal) => void;

	employeeFields: EmployeeField[];
	fetchEmployeeFields: () => void;

	isSummarizeRowsView: boolean;
	setIsSummarizeRowsView: (isSummarizeRowsView: boolean) => void;
	canViewSummary: boolean | undefined;
	setCanViewSummary: (canViewSummary: boolean | undefined) => void;
	fetchCanViewSummary: () => void;

	availableSummarizeFields: any[];
	fetchAvailableSummarizeFields: () => Promise<void>;
	setAvailableSummarizeFields: (availableSummarizeFields: { value: string; label: string }[]) => void;
	enabledSummarizeFields: { value: string; label: string; selection: string | null }[];
	setEnabledSummarizeFields: (
		enabledSummarizeFields: { value: string; label: string; selection: string | null }[]
	) => void;

	summaryFunctions: { [key: string]: SummaryFunction };
	setSummaryFunctions: (summaryFunctions: { [key: string]: SummaryFunction }) => void;
	getSummaryColumnMetadata: () => { [key: string]: ISummaryColumnMetadata };

	showAllAggregationColumns: boolean | null;
	setShowAllAggregationColumns: (showAllAggregationColumns: boolean) => void;
	calculateShowAllColumnsToggleValue: () => boolean;

	enabledGridColumns: string[];
	availableGridColumns: GridColumn[];
	_availableGridColumnsMap: { [key: string]: GridColumn } | null;
	setEnabledGridColumns: (enabledGridColumns: string[]) => void;

	// columnMetadata refers to column values that can be passed directly
	// to columnDefs in AgGrid to render the appropriate result. These values
	// will be saved to the database and will persist across sessions if saved.
	// e.g. width
	setColumnMetaData: (columnMetadata: { [key: string]: IColumnMetadata }) => void;
	columnMetadata: { [key: string]: IColumnMetadata };
	resetColumnsToDefault: () => void;

	_addColumnToEnabledGridColumns: (columnID: string) => void;
	toggleGridColumn: (columnID: string) => void;
	fetchAvailableColumns: () => Promise<void>;
	getEnabledGridColumns: () => GridColumn[];
	getEnabledSummarizeRowsColumns: () => GridColumn[];
	getAvailableGridColumns: () => DataSet[];

	// This state is used to store the currently expanded "superRow" cell in the grid.
	expandedSuperRowCell: { row: number; col: number } | null;
	expandingSuperRowCell: { row: number; col: number } | null;
	setExpandedSuperRowCell: (cell: { row: number; col: number } | null) => void;
	getExpandedSuperRowCell: () => { row: number; col: number } | null;
	setExpandingSuperRowCell: (cell: { row: number; col: number } | null) => void;
	getExpandingSuperRowCell: () => { row: number; col: number } | null;

	// Stores unique questions fetched from the server. It stores this data
	// in multiple formats for easier access: array, object where keys are the
	// question IDs and values are the questions, and a Fuse instance that is
	// used to fuzzy search the questions.
	hasGroupableQuestions: boolean; // if no groupable questions, we will inform the user in the modal
	uniqueReviewReportQuestions: IQuestion[];
	uniqueReviewReportQuestionsMap: { [key: string]: IQuestion };
	uniqueReviewReportQuestionsFuse: Fuse<IQuestion, FuseOptions<IQuestion>> | null;
	nonUniqueReviewReportQuestions: IQuestion[];
	nonUniqueReviewReportQuestionsMap: { [key: string]: IQuestion };
	fetchQuestions: (questionGroupingSessionID?: string) => Promise<void>;

	// Stores unique questions fetched from the server
	uniqueSurveyQuestions: ISurveyQuestion[];
	uniqueSurveyQuestionsMap: { [key: string]: ISurveyQuestion };
	uniqueSurveyQuestionsFuse: Fuse<ISurveyQuestion, FuseOptions<ISurveyQuestion>> | null;
	nonUniqueSurveyQuestions: ISurveyQuestion[];
	nonUniqueSurveyQuestionsMap: { [key: string]: ISurveyQuestion };
	fetchSurveyQuestions: (questionGroupingSessionID?: string) => Promise<void>;
	fetchGroupedSurveyQuestions: () => Promise<void>;

	// This object stores the state of various properties in the question
	// grouping modal.
	questionGroupingModal: {
		name: string;
		selectedQuestions: string[];
		showOnlySelectedQuestions: boolean;
		modalError: string | null;
		state: "NEW" | "EDIT";
		id?: string | null;

		// These are methods that are used to update or select the various
		// grouping modal properties.
		toggleSelectedQuestion: (questionID: string) => void;
		clearSelectedQuestions: () => void;
		clearName: () => void;
		clearShowSelectedOnly: () => void;
		setQuestionGroupModalValue: (
			props: { name?: string; selectedQuestions?: any; showOnlySelectedQuestions?: boolean },
			options?: { isAutoName?: boolean }
		) => void;
		getSelectedQuestionInformation: () => { type: string; reviewFormID: string } | null;
		getUniqueQuestions: (search?: string) => any[];
		setQuestionGroupModalError: (error: string) => void;
		setName: (name: string) => void;
		loadQuestionGroup: (questionID: string) => void;
		clearModalError: () => void;
		clearModal: () => void;
		clearLoadedQuestionGroup: () => void;
		getUniqueSurveyQuestions: (search?: string) => any[];
	};

	getSaveReportData: () => {
		name?: string;
		enabledColumns: string[] | null;
		columnMetadata: { [key: string]: IColumnMetadata };
		sorts: GridSort[];
		filters?: FieldFilter[][] | null;
		sharedWith?: string[];
	};

	// Pagination
	pagination: IReportPaginationParams;
	setPagination: (pagination: { pageNumber: number; pageSize: number }) => void;
}

// store to handle employee reports
export interface EmployeeReportStore {
	fetchAvailableEmployeeColumns: () => Promise<GridColumn[]>;
	getAvailableEmployeeSummarizeFields: () => IAvailableSummarizeField[];
	fetchEmployeeFilterOptions: () => Promise<FilterOptions>;
	fetchEmployeeReportCSV: () => Promise<void>;
}

export interface GoalReportStore {
	fetchAvailableGoalColumns: () => Promise<GridColumn[]>;
	fetchGoalFilterOptions: () => Promise<FilterOptions>;
	fetchGoalReportCSV: () => Promise<void>;
	fetchAvailableGoalSummarizeFields: () => IAvailableSummarizeField[];
}

export interface SurveyReportStore {
	fetchAvailableSurveyColumns: () => Promise<GridColumn[]>;
	fetchSurveyFilterOptions: () => Promise<FilterOptions>;
	fetchSurveyReportCSV: () => Promise<void>;
	getAvailableSurveySummarizeFields: () => IAvailableSummarizeField[];
	getUniqueSurveyQuestions: (search?: string) => any[];
}

// store to handle review reports
export interface ReviewReportStore {
	fetchAvailableReviewColumns: () => Promise<GridColumn[]>;
	fetchAvailableReviewSummarizeFields: () => Promise<IAvailableSummarizeField[]>;
	fetchReviewFilterOptions: () => Promise<FilterOptions>;
}

// store to handle charts
export interface ReportChartsStore {
	getAvailableCharts: () => ChartType[];
	selectedChart: ChartType;
	previousChart: ChartType; // used to handle carry over of chart options
	setSelectedChart: (chart: ChartType) => void;
	visualizeOptions: Partial<Record<ChartType, ChartOptions>>;
	setVisualizeOptions: (options: Partial<Record<ChartType, any>>, noChange?: boolean) => void;
	fallbackToTableChart: () => void;
	getSavedVisualizeOptions: () => Partial<Record<ChartType, ChartOptions>>;
}

export interface ReportStore
	extends
		ReportBaseStore,
		ReportChartsStore,
		EmployeeReportStore,
		GoalReportStore,
		SurveyReportStore,
		ReviewReportStore {}
